// Typed wrappers for the ListenBrainz "Discover" feature
// (`/api/v1/listenbrainz/*`). All endpoints are admin-only on the server.
//
// A seed is an artist the user has hand-picked. The backend expands each seed
// via ListenBrainz's similar-artists feature and queues the discovered artists'
// albums. The shape of these requests/responses mirrors the contract shipped
// by the parallel `arr-listenbrainz` agent.

import { api } from "@/lib/api";

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

export interface AddSeedRequest {
  artist_mbid: string;
  /** Default 25, range 1–100. */
  max_similar?: number;
  /** Default 5, range 1–20. */
  max_albums?: number;
  /** Default false. */
  include_eps?: boolean;
}

export interface SeedSummary {
  id: number;
  artist_mbid: string;
  artist_name: string;
  max_similar: number;
  max_albums: number;
  include_eps: boolean;
  last_expanded_at: string | null;
  derived_artists_count: number;
  derived_albums_count: number;
  created_at: string;
}

export interface PreviewArtist {
  mbid: string;
  name: string;
  /** 0–1 from ListenBrainz; UI shows it to one decimal. */
  similarity_score: number;
  already_in_library: boolean;
  album_count_in_mb: number;
}

export interface ExpansionPreview {
  similar_artists_count: number;
  estimated_albums: number;
  already_in_library: number;
  /** Top-N (server picks N, typically 10) by similarity for the preview UI. */
  sample: PreviewArtist[];
}

export interface ExpansionFailure {
  artist_mbid: string;
  artist_name: string;
  reason: string;
}

export interface ExpansionResult {
  artists_added: number;
  artists_skipped_already_present: number;
  albums_queued: number;
  failures: ExpansionFailure[];
}

export interface DerivedArtist {
  artist_mbid: string;
  artist_name: string;
  /** FK into the local `artists` table. May be null until the artist gets
   *  hydrated by the music importer. */
  artist_id: number | null;
  similarity_score: number | null;
  albums_added: number;
  derived_at: string;
}

// -----------------------------------------------------------------------------
// API
// -----------------------------------------------------------------------------

export const listenbrainzApi = {
  seeds: {
    list: () => api<SeedSummary[]>("/api/v1/listenbrainz/seeds"),
    add: (body: AddSeedRequest) =>
      api<SeedSummary>("/api/v1/listenbrainz/seeds", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    remove: (id: number) =>
      api<void>(`/api/v1/listenbrainz/seeds/${id}`, { method: "DELETE" }),
    /** Re-expand a seed against the latest similar-artists data. */
    refresh: (id: number) =>
      api<ExpansionResult>(`/api/v1/listenbrainz/seeds/${id}/refresh`, {
        method: "POST",
      }),
    derived: (id: number) =>
      api<DerivedArtist[]>(`/api/v1/listenbrainz/seeds/${id}/derived`),
  },

  /** Dry-run an expansion. Same body as Add, but doesn't persist anything. */
  preview: (body: AddSeedRequest) =>
    api<ExpansionPreview>("/api/v1/listenbrainz/preview", {
      method: "POST",
      body: JSON.stringify(body),
    }),
};
